import { ComponentProps, useState } from "react"
import { Control, Controller, FieldError, FieldPath, FieldValues } from "react-hook-form"
import { Button, Table, TableBody, TableCell, TableRow, TextField } from "@mui/material"

type OptionalTextFieldArrayProps<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = Omit<ComponentProps<typeof TextField>, 'label'> & {
  control: Control<TFieldValues>;
  /** e.g. `Middlenames` where `Middlenames` holds an object which has the `string[]` within it */
  name: TName,
  /** e.g. `Middlenames.Middlename`, where `Middlename` is the `string[]` */
  arrayName: TName,
  /** e.g. `Middlename` */
  labelSingular: ComponentProps<typeof TextField>['label'],
  /** e.g. `Middlenames` */
  labelPlural: ComponentProps<typeof TextField>['label'],
  /**
   * The maximum array size. The add button disappears when this size is reached.
   * Might be extractable from the zod schema.
   * e.g. `zLocalFormSchema.shape.Middlenames.unwrap().shape.Middlename._def.maxLength?.value`
   */
  max?: number,
  /**
   * We store the state as an array of strings, but we need to convert it to the form value for storage.
   * This function is called with `string[]` and should return the form value.
   */
  convertLocalStateToFormValue: (state: string[]) => ReturnType<Control<TFieldValues>['_formValues'][TName]>,
  /**
   * We initialise the state from the form value, but we need to convert it to the state format of a `string[]`.
   */
  convertFormValueToLocalState: (value: ReturnType<Control<TFieldValues>['_formValues'][TName]>) => string[],
  /**
   * Depending on how the state is structured, we might need to dig out the error for this field from the form.
   * @param error The FieldError for the field named in `name`
   * @param index The index of the individual text field
   * @returns undefined if there is no error for this text field, otherwise, return the error for this text field
   */
  getFieldError: (error: FieldError | undefined, index: number) => any,
}

const OptionalTextFieldArray: React.FC<OptionalTextFieldArrayProps> = (props) => {
  const { control, name, arrayName, labelSingular, labelPlural, max, convertLocalStateToFormValue: convertStateToValue, convertFormValueToLocalState: convertValueToState, getFieldError, ...textFieldProps } = props

  const watchedValue = control._getWatch(name)

  const [localState, setLocalState] = useState<string[]>(() => convertValueToState(watchedValue))

  const addField = (onChange: (newValue: ReturnType<typeof convertStateToValue>) => void) => {
    const updatedState = [...localState, '']
    setLocalState(updatedState)

    const updatedValue = convertStateToValue(updatedState)
    onChange(updatedValue)
  }

  const updateField = (index: number, newValue: string, onChange: (newValue: ReturnType<typeof convertStateToValue>) => void) => {
    const updatedState = [...localState]
    updatedState[index] = newValue
    setLocalState(updatedState)

    const updatedValue = convertStateToValue(updatedState)
    onChange(updatedValue)
  }

  const removeField = (index: number, onChange: (newValue: ReturnType<typeof convertStateToValue>) => void) => {
    const updatedState = [...localState]
    updatedState.splice(index, 1)
    setLocalState(updatedState)

    const updatedValue = convertStateToValue(updatedState)
    onChange(updatedValue)
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange }, fieldState: { error } }) => {
        const canAddMore = (max == null || localState.length < max) && !props.disabled

        return <>
          <Table padding="none">
            <TableBody>
              {localState.map((value, index) => {
                const fieldError = getFieldError(error, index)
                return (
                  <TableRow key={index}>
                    <TableCell style={{ borderBottom: "none" }}>
                      <TextField
                        {...textFieldProps}
                        label={labelSingular}
                        value={value}
                        fullWidth
                        sx={{ marginTop: 1 }}
                        onChange={event => {
                          const newValue = event.target.value
                          updateField(index, newValue, onChange)
                        }}
                        error={!!fieldError}
                        helperText={fieldError && fieldError.message}
                      />
                      <input type="hidden" name={`${arrayName}.${index}`} value={value} />
                    </TableCell>
                    {!props.disabled && (
                      <TableCell style={{ borderBottom: "none" }}>
                        <Button sx={{ marginLeft: 1 }} data-testid={`remove-${name}`} onClick={() => removeField(index, onChange)}>Remove</Button>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          {canAddMore && (
            <Button
              type="button"
              sx={{ marginTop: 1 }}
              onClick={() => addField(onChange)}
            >
              Add {localState.length === 0 ? labelPlural : `Another ${labelSingular}`}
            </Button>
          )}
        </>
      }}
    />
  )
}

export default OptionalTextFieldArray
